import {useEffect, useState} from "react";
import {readObjectContext, type ObjectContext} from "../../api/client";
import {readProductWorkspace, type ProductWorkspaceProjection} from "../../api/productWorkspace";
import {useAuth} from "../../auth/AuthContext";
import {AsyncState} from "../../components/AsyncState";
import {ContextHeader} from "../../components/ContextHeader";
import {WorkspaceShell} from "../../components/Workspace";

type ObjectType = "PROGRAM" | "PRODUCT" | "CAPABILITY" | "WORK_PACKAGE" | "EXECUTION_PLAN";

export function ObjectWorkspace({objectType, objectId}: {objectType: ObjectType; objectId: string}) {
  const {authorizedFetch} = useAuth();
  const [context, setContext] = useState<ObjectContext | null>(null);
  const [error, setError] = useState<{kind: "error" | "restricted"; detail: string} | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setContext(null);
    setError(null);
    void readObjectContext(authorizedFetch, objectType, objectId, controller.signal)
      .then(setContext)
      .catch(problem => {
        if (!controller.signal.aborted) {
          const detail = message(problem);
          setError({kind: /permission|required|forbidden/i.test(detail) ? "restricted" : "error", detail});
        }
      });
    return () => controller.abort();
  }, [authorizedFetch, objectId, objectType]);
  if (error) return <AsyncState kind={error.kind} detail={error.detail} />;
  if (!context) return <AsyncState kind="loading" detail="Resolving authorized object context and available actions." />;
  if (objectType === "PRODUCT") return <ProductContextWorkspace context={context} />;
  return <WorkspaceShell context={<ContextHeader context={context} />}
    navigation={<>{sections(objectType).map(section => <a key={section} href={`#${slug(section)}`}>{section}</a>)}</>}>
    <section id="overview" className="panel workspace-foundation">
      <h2>Workspace foundation</h2>
      <p>This durable route resolves the governed object and its authorized context from live server data. Full domain sections arrive in their accepted implementation wave.</p>
      <dl><div><dt>Object ID</dt><dd><code>{context.subject.id}</code></dd></div>
        <div><dt>Tenant</dt><dd><code>{context.tenantId}</code></dd></div>
        <div><dt>Freshness</dt><dd>{context.freshness.replaceAll("_", " ")}</dd></div></dl>
    </section>
  </WorkspaceShell>;
}

function ProductContextWorkspace({context}: {context: ObjectContext}) {
  const {authorizedFetch} = useAuth();
  const [workspace, setWorkspace] = useState<ProductWorkspaceProjection | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    void readProductWorkspace(authorizedFetch, context.subject.key, controller.signal)
      .then(setWorkspace)
      .catch(reason => { if (!controller.signal.aborted) setError(message(reason)); });
    return () => controller.abort();
  }, [authorizedFetch, context.subject.key]);
  return <WorkspaceShell context={<ContextHeader context={context} />}
    navigation={<>{["Overview","Requirements","Dependencies","Authority","Work","Evidence","Human decisions"]
      .map(section => <a key={section} href={`#${slug(section)}`}>{section}</a>)}</>}>
    {error && <p className="form-message error" role="alert">{error}</p>}
    {!workspace && !error && <p role="status">Loading contextual Product Workspace…</p>}
    {workspace && <>
      <section id="overview" className="panel workspace-foundation"><h2>Product engineering context</h2>
        <p>{workspace.productKey} · {workspace.lifecycle} · v{workspace.version}</p>
        <dl>{Object.entries(workspace.governedObjectCounts).map(([label, value]) =>
          <div key={label}><dt>{human(label)}</dt><dd>{value}</dd></div>)}</dl>
        <p>Active baseline: <strong>{baselineTitle(workspace.activeBaseline)}</strong></p></section>
      <WorkspaceItems id="requirements" title="Requirements and gaps"
        items={[...workspace.requirements, ...workspace.gaps]} />
      <WorkspaceItems id="dependencies" title="Dependencies"
        items={[...workspace.dependencies, ...workspace.platformDependencies]} />
      <WorkspaceObjects title="Program and capability context" objects={workspace.governedObjects
        .filter(item => item.objectType === "PROGRAM" || item.objectType === "CAPABILITY")} />
      <section id="authority" className="panel workspace-foundation"><h2>Repository authority and context</h2>
        <p>{workspace.contextPackages} immutable Context Package(s).</p>
        {workspace.repositoryAuthorities.length === 0 ? <p>No accepted authority binding.</p> : <ul>
          {workspace.repositoryAuthorities.map(authority => <li key={authority.id}>
            <strong>{authority.authorityKey}</strong> · {authority.semantics} · {authority.lifecycle}
            <br /><small>{authority.repositoryUri || "OWNER_DECISION_REQUIRED"} {authority.commitSha}</small>
          </li>)}</ul>}</section>
      <WorkspaceItems id="work" title="Implementation, proposals, and defects"
        items={[...workspace.targetImplementation, ...workspace.changeProposals,
          ...workspace.aiProposals, ...workspace.defects]} />
      <WorkspaceObjects title="Active governed work and execution" objects={workspace.governedObjects
        .filter(item => ["WORK_PACKAGE", "EXECUTION_PLAN", "EXECUTION_RUN"].includes(item.objectType))} />
      <section id="evidence" className="panel workspace-foundation"><h2>Evidence and review</h2>
        <p>{workspace.evidenceItems} contextual evidence item(s); {workspace.governedObjectCounts.executionEvidence ?? 0} execution evidence reference(s); {workspace.governedObjectCounts.executionReviews ?? 0} execution review(s).</p></section>
      <WorkspaceObjects title="Execution evidence and reviews" objects={workspace.governedObjects
        .filter(item => item.objectType === "EVIDENCE" || item.objectType === "REVIEW")} />
      <section id="human-decisions" className="panel workspace-foundation"><h2>Pending human decisions</h2>
        {workspace.pendingHumanDecisions.length === 0 ? <p>No submitted decisions.</p> : <ul>
          {workspace.pendingHumanDecisions.map(item => <li key={item}>{item}</li>)}</ul>}</section>
    </>}
  </WorkspaceShell>;
}

function WorkspaceObjects({title, objects}: {title: string; objects: Array<{
  objectType: string; id: string; key: string; title: string; lifecycle: string;
  version: number; parentKey: string;
}>}) {
  return <section className="panel workspace-foundation"><h2>{title}</h2>
    {objects.length === 0 ? <p>None recorded.</p> : <ul>{objects.map(item => <li key={item.id}>
      <strong>{item.key} — {item.title}</strong><br />
      <small>{item.objectType} · {item.lifecycle} · v{item.version}
        {item.parentKey ? ` · parent ${item.parentKey}` : ""}</small>
    </li>)}</ul>}
  </section>;
}

function WorkspaceItems({id, title, items}: {id: string; title: string; items: Array<{
  id: string; itemKey: string; title: string; itemType: string; lifecycle: string;
}>}) {
  return <section id={id} className="panel workspace-foundation"><h2>{title}</h2>
    {items.length === 0 ? <p>None recorded.</p> : <ul>{items.map(item => <li key={item.id}>
      <strong>{item.itemKey} — {item.title}</strong><br /><small>{item.itemType} · {item.lifecycle}</small>
    </li>)}</ul>}</section>;
}

function sections(type: ObjectType) {
  return type === "PROGRAM" ? ["Overview", "Products", "Work Packages", "History", "Audit"]
    : type === "WORK_PACKAGE" ? ["Overview", "Intent", "Versions", "Approval", "Execution Plans", "Audit"]
    : type === "EXECUTION_PLAN" ? ["Overview", "Scope", "Tasks", "Workers", "Evidence", "Approval", "Audit"]
    : ["Overview", "Relationships", "Work", "Activity"];
}

function slug(value: string) { return value.toLowerCase().replaceAll(" ", "-"); }
function human(value: string) { return value.replace(/([A-Z])/g, " $1").replace(/^./, letter => letter.toUpperCase()); }
function baselineTitle(value: unknown) {
  if (value && typeof value === "object" && "title" in value && typeof value.title === "string") return value.title;
  return "Not accepted";
}
function message(problem: unknown) { return problem instanceof Error ? problem.message : "Object context failed"; }
